import { useEffect, useState, useMemo } from 'react';
import { Icon } from '@iconify/react';
import closeIcon from '@iconify/icons-material-symbols/close-rounded';
import * as XLSX from 'xlsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/Dialog';
import CustomDropdown from '../AdminReportComponents/CustomDropdown';
import Table from '../Tables/Table';
import { getLegacyStudentLogins, getNewStudentLogins, getLegacyStoryOpens, getNewStoryOpens } from '../reportUtilities';

interface classroomData {
  classroomId: string;
  className: string;
  grade: number;
  listOfStudentProxies?: Array<{
    firstName: string;
    lastName?: string;
    middleName?: string;
    userId: string;
    username: string;
    grade: number;
  }>;
}

interface StudentData {
  userId: string;
  firstName: string;
  lastName?: string;
  middleName?: string;
  username: string;
  grade: number;
}

interface StudentWithStats extends StudentData {
  logins: number;
  feelingsCheckins: number;
  extraCol1: number;
  extraCol2: number;
}

interface AllEvents {
  userId?: string;
  distinct_id?: string;
}

interface JournalsMap {
  [key: string]: unknown[];
}

interface TeacherClassroomModalProps {
  isOpen: boolean;
  onClose: () => void;
  teacherName: string;
  teacherEmail: string;
  classrooms: classroomData[];
  allEvents: AllEvents[];
  journalsMap: JournalsMap;
  minGrade?: number;
  maxGrade?: number;
  gameProgressMap?: Record<string, Array<{
    completionPercent: number;
    displayName: string;
    id: string;
    lastDateOpened: number;
    readingLevel: number;
    timeSpent: number;
    type: string;
  }>>;
  assignmentsMap?: Record<string, Array<{ status: string }>>;
}

const TeacherClassroomModal = ({
  isOpen,
  onClose,
  teacherName,
  teacherEmail,
  classrooms,
  allEvents,
  journalsMap,
  minGrade = -1,
  maxGrade = 999,
  gameProgressMap,
  assignmentsMap,
}: TeacherClassroomModalProps) => {
  const [selectedClassroomId, setSelectedClassroomId] = useState<string>('');

  const filteredClassrooms = useMemo(
    () =>
      classrooms.filter((c) => {
        const grade = parseInt(String(c.grade));
        return grade >= minGrade && grade <= maxGrade;
      }),
    [classrooms, minGrade, maxGrade],
  );

  const classroomOptions = useMemo(
    () =>
      filteredClassrooms.map((c) => ({
        id: c.classroomId,
        name: `${c.className} (Grade ${c.grade})`,
      })),
    [filteredClassrooms],
  );

  const selectedClassroom = useMemo(
    () => filteredClassrooms.find((c) => c.classroomId === selectedClassroomId),
    [filteredClassrooms, selectedClassroomId],
  );

  const selectedGrade = selectedClassroom ? parseInt(String(selectedClassroom.grade)) : undefined;
  const variant = selectedGrade !== undefined
    ? (selectedGrade <= 5 && gameProgressMap ? 'elementary'
        : selectedGrade >= 6 && assignmentsMap ? 'secondary'
        : 'summary')
    : gameProgressMap ? 'elementary'
      : assignmentsMap ? 'secondary'
      : 'summary';

  const extraCol1Label = variant === 'elementary' ? 'Activities Completed' : variant === 'secondary' ? 'Assignments Completed' : null;
  const extraCol2Label = variant === 'elementary' ? 'Book Opens' : variant === 'secondary' ? 'Story Opens' : null;

  useEffect(() => {
    if (isOpen && filteredClassrooms.length > 0) {
      setSelectedClassroomId(filteredClassrooms[0].classroomId);
    }
  }, [isOpen, filteredClassrooms]);

  const getClassroomStudents = (classroom?: classroomData): StudentData[] => {
    const target = classroom || selectedClassroom;
    if (!target?.listOfStudentProxies) {
      return [];
    }
    return target.listOfStudentProxies.map((s) => ({
      userId: s.userId,
      firstName: s.firstName,
      lastName: s.lastName || '',
      middleName: s.middleName || '',
      username: s.username,
      grade: s.grade,
    }));
  };

  const getStudentLogins = (student: StudentData) => {
    const studentEvents = allEvents.filter((e) => (e.userId || e.distinct_id) === student.userId);
    return getLegacyStudentLogins(studentEvents) + getNewStudentLogins(studentEvents);
  };

  const getStudentFeelingsCheckins = (student: StudentData) => {
    const journals = journalsMap[student.userId];
    return journals?.length || 0;
  };

  const getStudentExtraCol1 = (student: StudentData): number => {
    if (variant === 'elementary' && gameProgressMap) {
      const progress = gameProgressMap[student.userId];
      if (!progress || !Array.isArray(progress)) return 0;
      return progress.reduce((acc, g) => {
        if (g?.type === 'mini-game' && g?.completionPercent > 0) return acc + 1;
        return acc;
      }, 0);
    }
    if (variant === 'secondary' && assignmentsMap) {
      const assignments = assignmentsMap[student.userId];
      if (!assignments || !Array.isArray(assignments)) return 0;
      return assignments.filter((a) => a.status === 'submitted').length;
    }
    return 0;
  };

  const getStudentExtraCol2 = (student: StudentData): number => {
    const studentEvents = allEvents.filter((e) => (e.userId || e.distinct_id) === student.userId);
    return getLegacyStoryOpens(studentEvents) + getNewStoryOpens(studentEvents);
  };

  const getStudentsWithStats = (students: StudentData[]): StudentWithStats[] => {
    return students.map((s) => ({
      ...s,
      logins: getStudentLogins(s),
      feelingsCheckins: getStudentFeelingsCheckins(s),
      extraCol1: getStudentExtraCol1(s),
      extraCol2: getStudentExtraCol2(s),
    }));
  };

  const classroomStudents = useMemo(() => getClassroomStudents(), [selectedClassroom]);
  const studentDataWithStats = useMemo(
    () => getStudentsWithStats(classroomStudents),
    [classroomStudents, allEvents, journalsMap, gameProgressMap, assignmentsMap],
  );

  const totalLogins = studentDataWithStats.reduce((sum, s) => sum + s.logins, 0);
  const totalFeelingsCheckins = studentDataWithStats.reduce((sum, s) => sum + s.feelingsCheckins, 0);
  const totalExtraCol1 = studentDataWithStats.reduce((sum, s) => sum + s.extraCol1, 0);
  const totalExtraCol2 = studentDataWithStats.reduce((sum, s) => sum + s.extraCol2, 0);

  const columnCount = extraCol1Label ? 5 : 3;

  const sanitizeSheetName = (name: string): string => {
    return name.replace(/[\\\/\?\*\[\]\:]/g, '').slice(0, 31);
  };

  const buildHeader = (): string[] => {
    const header = ['Student Name', 'Logins', 'Feelings Check-Ins'];
    if (extraCol1Label) header.push(extraCol1Label);
    if (extraCol2Label) header.push(extraCol2Label);
    return header;
  };

  const buildClassroomSheetData = (classroom: classroomData): string[][] => {
    const students = getClassroomStudents(classroom);
    const studentsWithStats = getStudentsWithStats(students);
    const header = buildHeader();
    const rows = studentsWithStats.map((s) => {
      const row = [
        `${s.firstName} ${s.lastName || ''}`.trim(),
        String(s.logins),
        String(s.feelingsCheckins),
      ];
      if (extraCol1Label !== null) row.push(String(s.extraCol1));
      if (extraCol2Label !== null) row.push(String(s.extraCol2));
      return row;
    });
    const totals = ['Total', String(totalLogins), String(totalFeelingsCheckins)];
    if (extraCol1Label !== null) totals.push(String(totalExtraCol1));
    if (extraCol2Label !== null) totals.push(String(totalExtraCol2));
    return [header, ...rows, totals];
  };

  const downloadCurrentClassroom = () => {
    if (!selectedClassroom) return;
    const data = buildClassroomSheetData(selectedClassroom);
    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    const sheetName = sanitizeSheetName(selectedClassroom.className);
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    const fileName = `${teacherName.replace(/[^a-zA-Z0-9\s\-]/g, '')}-${selectedClassroom.className.replace(/[^a-zA-Z0-9\s\-]/g, '')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const downloadAllClassrooms = () => {
    if (filteredClassrooms.length === 0) return;
    const wb = XLSX.utils.book_new();
    filteredClassrooms.forEach((c) => {
      const data = buildClassroomSheetData(c);
      const ws = XLSX.utils.aoa_to_sheet(data);
      const sheetName = sanitizeSheetName(c.className);
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });
    const fileName = `${teacherName.replace(/[^a-zA-Z0-9\s\-]/g, '')}-All-Classrooms.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const getTableContent = () => {
    if (!selectedClassroomId) {
      return (
        <tr>
          <td colSpan={columnCount} className='text-center p-8 text-gray-500'>
            Please select a classroom to view student data
          </td>
        </tr>
      );
    }

    if (studentDataWithStats.length === 0) {
      return (
        <tr>
          <td colSpan={columnCount} className='text-center p-8 text-gray-500'>
            No students in this classroom
          </td>
        </tr>
      );
    }

    return (
      <>
        {studentDataWithStats.map((student) => (
          <tr key={student.userId}>
            <td>{`${student.firstName} ${student.lastName || ''}`.trim()}</td>
            <td>{student.logins}</td>
            <td>{student.feelingsCheckins}</td>
            {extraCol1Label !== null && <td>{student.extraCol1}</td>}
            {extraCol2Label !== null && <td>{student.extraCol2}</td>}
          </tr>
        ))}
        <tr className='bg-gray-50'>
          <td className="font-['GT-Walsheim-Pro-Bold']">Total</td>
          <td className="font-['GT-Walsheim-Pro-Bold']">{totalLogins}</td>
          <td className="font-['GT-Walsheim-Pro-Bold']">{totalFeelingsCheckins}</td>
          {extraCol1Label !== null && <td className="font-['GT-Walsheim-Pro-Bold']">{totalExtraCol1}</td>}
          {extraCol2Label !== null && <td className="font-['GT-Walsheim-Pro-Bold']">{totalExtraCol2}</td>}
        </tr>
      </>
    );
  };

  const buildTableHeader = () => {
    const headers = [{ title: 'Student Name' }, { title: 'Logins' }, { title: 'Feelings Check-Ins' }];
    if (extraCol1Label) headers.push({ title: extraCol1Label });
    if (extraCol2Label) headers.push({ title: extraCol2Label });
    return headers;
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        className='w-[min(92vw,60rem)] h-[80vh] rounded-[1.6rem] border-0 bg-white px-[3rem] pb-[3rem] pt-[3rem] shadow-[0_14px_34px_rgba(0,0,0,0.2)]'
        overlayClassName='bg-black/50'
      >
        <button
          type='button'
          aria-label='Close'
          className='absolute right-[1rem] top-[1rem] flex h-[3rem] w-[3rem] items-center justify-center rounded-full border-0 bg-[#e5e5e5] p-[0.5rem]'
          onClick={onClose}
        >
          <Icon className='h-[2rem] w-[2rem] cursor-pointer' icon={closeIcon} />
        </button>
        <DialogHeader>
          <DialogTitle>{teacherName}&apos;s Classroom Data</DialogTitle>
        </DialogHeader>

        {classroomOptions.length > 0 ? (
          <div className='mb-4 border border-gray-300 rounded p-1'>
            <CustomDropdown
              list={classroomOptions}
              onSelectSubject={(value) => setSelectedClassroomId(value || '')}
              initialValue={classroomOptions[0]?.id || ''}
              currentlySelectedItem={selectedClassroomId}
              disableChangeLocation={false}
              icon='icon-park-outline:classroom'
              placeholder='Select a classroom'
            />
          </div>
        ) : (
          <div className='mb-4 p-4 text-center text-gray-500'>No classrooms available</div>
        )}

        <div className='flex gap-4 mb-4'>
          <button
            type='button'
            onClick={downloadCurrentClassroom}
            disabled={!selectedClassroomId || studentDataWithStats.length === 0}
            className="flex items-center gap-2 rounded-md border-2 border-[#00afd8] bg-white px-5 py-3 font-['GT-Walsheim-Pro-Bold'] text-[1.4rem] uppercase tracking-[0.02em] text-[#00afd8] transition-[background_250ms_ease-in-out] hover:bg-[#e6f9fd] focus:outline-none focus:ring-2 focus:ring-[#009fc4] disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Icon icon='material-symbols:download-rounded' className='text-[2.4rem]' />
            Download Current Classroom Data
          </button>
          <button
            type='button'
            onClick={downloadAllClassrooms}
            disabled={filteredClassrooms.length === 0}
            className="flex items-center gap-2 rounded-md border-2 border-[#00afd8] bg-white px-5 py-3 font-['GT-Walsheim-Pro-Bold'] text-[1.4rem] uppercase tracking-[0.02em] text-[#00afd8] transition-[background_250ms_ease-in-out] hover:bg-[#e6f9fd] focus:outline-none focus:ring-2 focus:ring-[#009fc4] disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Icon icon='material-symbols:download-rounded' className='text-[2.4rem]' />
            Download All Classroom Data
          </button>
        </div>

        {selectedClassroom && (
          <div className='max-h-[calc(80vh-280px)] overflow-y-auto'>
            <Table tableHeader={buildTableHeader()}>
              {getTableContent()}
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TeacherClassroomModal;
